import { EMAIL_CONTACT } from "@/lib/constants/classes";

export type ContentSettingsPlaceholderContext = {
  studioName?: string | null;
  tenantEmail?: string | null;
  userEmail?: string | null;
  className?: string | null;
  seasonName?: string | null;
};

export const CONTENT_SETTINGS_NO_CONTACT_FALLBACK =
  "Please check back later or contact us for more information.";

export type ContentSettingsTextSegment =
  | { type: "text"; value: string }
  | { type: "email"; value: string; href: string };

export function resolveTenantEmail(tenantEmail?: string | null): {
  email: string;
  isPlaceholder: boolean;
} {
  const email = tenantEmail?.trim() || EMAIL_CONTACT;
  return { email, isPlaceholder: email === EMAIL_CONTACT };
}

/** Plain-string interpolation (no mailto links). */
export function interpolateContentSettingsString(
  template: string,
  ctx: ContentSettingsPlaceholderContext,
): string {
  const studio = ctx.studioName?.trim() || "us";
  const classLabel = ctx.className?.trim() || "this class";
  const seasonLabel = ctx.seasonName?.trim() || "this season";
  const { email, isPlaceholder } = resolveTenantEmail(ctx.tenantEmail);

  let result = template
    .replace(/\{studioName\}/g, studio)
    .replace(/\{className\}/g, classLabel)
    .replace(/\{seasonName\}/g, seasonLabel);

  if (result.includes("{userEmail}")) {
    result = result.replace(/\{userEmail\}/g, ctx.userEmail?.trim() || "");
  }

  if (result.includes("{email}")) {
    result = result.replace(/\{email\}/g, isPlaceholder ? "us" : email);
  }

  return result;
}

export function parseContentSettingsTemplate(
  template: string,
  ctx: ContentSettingsPlaceholderContext,
  options?: { fallbackWithoutContact?: string },
):
  | { kind: "fallback"; text: string }
  | { kind: "segments"; segments: ContentSettingsTextSegment[] } {
  const fallbackWithoutContact =
    options?.fallbackWithoutContact ?? CONTENT_SETTINGS_NO_CONTACT_FALLBACK;
  const { email, isPlaceholder } = resolveTenantEmail(ctx.tenantEmail);

  if (isPlaceholder && template.includes("{email}")) {
    return { kind: "fallback", text: fallbackWithoutContact };
  }

  const studio = ctx.studioName?.trim() || "us";
  const classLabel = ctx.className?.trim() || "this class";
  const seasonLabel = ctx.seasonName?.trim() || "this season";

  let processed = template
    .replace(/\{studioName\}/g, studio)
    .replace(/\{className\}/g, classLabel)
    .replace(/\{seasonName\}/g, seasonLabel);

  if (processed.includes("{userEmail}")) {
    processed = processed.replace(/\{userEmail\}/g, ctx.userEmail?.trim() || "");
  }

  if (!processed.includes("{email}")) {
    return { kind: "segments", segments: [{ type: "text", value: processed }] };
  }

  const parts = processed.split("{email}");
  const segments: ContentSettingsTextSegment[] = [];

  parts.forEach((part, index) => {
    if (part) {
      segments.push({ type: "text", value: part });
    }
    if (index < parts.length - 1) {
      segments.push({ type: "email", value: email, href: `mailto:${email}` });
    }
  });

  return { kind: "segments", segments };
}
